/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorPlugins } from '../../types/EditorPlugins';
import { plugins } from '@ckeditor/ckeditor5-metadata';

import {
	mainToolbar,
	imageToolbar,
	tableToolbar,
	type ToolbarItem,
	type AnyToolbarItem
} from '../../constants/toolbars';

type ToolbarArgs = {
	editorPlugins: EditorPlugins;
	isMenuBarOn?: boolean;
	essentialOnly?: boolean;
};

export function getMainToolbar( { editorPlugins, isMenuBarOn = false, essentialOnly = false }: ToolbarArgs ): Array<string> {
	return getToolbarGeneric( {
		editorPlugins,
		toolbarData: mainToolbar,
		filterCallback: item => {
			if ( isMenuBarOn && !essentialOnly ) {
				return true;
			}

			if ( isMenuBarOn && item.removeWhenMenuBarOn ) {
				return false;
			}

			if ( hasIncompatiblePlugins( item, editorPlugins ) ) {
				return false;
			}

			return true;
		} }
	);
}

export function getBlockToolbar( { editorPlugins, isMenuBarOn = false }: ToolbarArgs ): Array<string> {
	return getToolbarGeneric( {
		editorPlugins,
		toolbarData: mainToolbar,
		filterCallback: item => {
			if ( isMenuBarOn && item.removeWhenMenuBarOn ) {
				return false;
			}

			if ( !item.inAdditionalBlockToolbar ) {
				return false;
			}

			if ( hasIncompatiblePlugins( item, editorPlugins ) ) {
				return false;
			}

			return true;
		} }
	);
}

export function getBalloonToolbar( { editorPlugins, isMenuBarOn = false }: ToolbarArgs ): Array<string> {
	return getToolbarGeneric( {
		editorPlugins,
		toolbarData: mainToolbar,
		filterCallback: item => {
			if ( isMenuBarOn && item.removeWhenMenuBarOn ) {
				return false;
			}

			if ( !item.inAdditionalBalloonToolbar ) {
				return false;
			}

			if ( hasIncompatiblePlugins( item, editorPlugins ) ) {
				return false;
			}

			return true;
		} }
	);
}

export function getImageToolbar( { editorPlugins }: { editorPlugins: EditorPlugins } ): Array<string> {
	return getToolbarGeneric( { editorPlugins, toolbarData: imageToolbar } );
}

export function getTableToolbar( { editorPlugins }: { editorPlugins: EditorPlugins } ): Array<string> {
	return getToolbarGeneric( { editorPlugins, toolbarData: tableToolbar } );
}

function getToolbarGeneric<ToolbarItemType extends AnyToolbarItem>( {
	toolbarData,
	editorPlugins,
	filterCallback = () => true
}: {
	toolbarData: Array<Array<ToolbarItemType>>;
	editorPlugins: EditorPlugins;
	filterCallback?: ( arg: ToolbarItemType ) => boolean;
} ) {
	const availableUiComponents = editorPlugins
		.map( editorPlugin => plugins.find( plugin => plugin.className === editorPlugin )! )
		.flatMap( editorPlugin => editorPlugin.uiComponents.map( uiComponent => uiComponent.name ) );

	return toolbarData.reduce( ( outputToolbar, toolbarGroup ) => {
		const validToolbarItems = toolbarGroup
			.filter( item => {
				if ( !availableUiComponents.includes( item.name ) ) {
					return false;
				}

				if ( 'filterCallback' in item && item.filterCallback ) {
					return item.filterCallback( editorPlugins );
				}

				return true;
			} )
			.filter( filterCallback );

		if ( outputToolbar.length && validToolbarItems.length ) {
			outputToolbar.push( '|' );
		}

		validToolbarItems.forEach( item => outputToolbar.push( item.name ) );

		return outputToolbar;
	}, [] as Array<string> );
}

function hasIncompatiblePlugins( item: ToolbarItem, editorPlugins: EditorPlugins ): boolean {
	if ( !item.incompatiblePlugins ) {
		return false;
	}

	return item.incompatiblePlugins.some( incompatiblePlugin => editorPlugins.includes( incompatiblePlugin ) );
}
